<script setup lang="ts">
import { Link } from '@inertiajs/vue3'
import { Play, Bookmark, Video } from 'lucide-vue-next'

interface Content {
  id: number
  slug: string
  title: string
  thumbnail: string | null
  tutor_name: string
  tutor_avatar: string | null
  duration: string
  subject: string
  type?: string
  progress?: number
  is_new?: boolean
  is_saved?: boolean
  updated_at?: string
}

defineProps<{
  contents: Content[]
}>()

function getGradientClass(index: number): string {
  const gradients = [
    'from-blue-500 to-blue-600',
    'from-green-500 to-green-600',
    'from-purple-500 to-purple-600',
    'from-orange-500 to-orange-600',
  ]
  return gradients[index % gradients.length]
}

function getSubjectBadgeClass(subject: string): string {
  const subjectLower = subject.toLowerCase()
  if (subjectLower.includes('math')) return 'bg-blue-100 text-blue-700'
  if (subjectLower.includes('chemistry')) return 'bg-green-100 text-green-700'
  if (subjectLower.includes('physics')) return 'bg-purple-100 text-purple-700'
  if (subjectLower.includes('english')) return 'bg-orange-100 text-orange-700'
  return 'bg-gray-100 text-gray-700'
}

function getButtonClass(index: number): string {
  const colors = [
    'bg-blue-600 hover:bg-blue-700',
    'bg-green-600 hover:bg-green-700',
    'bg-purple-600 hover:bg-purple-700',
    'bg-orange-600 hover:bg-orange-700',
  ]
  return colors[index % colors.length]
}

function getProgressBarClass(index: number): string {
  const colors = [
    'bg-blue-600',
    'bg-green-600',
    'bg-purple-600',
    'bg-orange-600',
  ]
  return colors[index % colors.length]
}
</script>

<template>
  <section>
    <div class="flex items-center justify-between mb-5">
      <h2 class="text-xl font-bold text-gray-900">My Content Library</h2>
      <Link href="/student/resources" class="text-blue-600 font-medium text-sm hover:underline">View All</Link>
    </div>

    <div v-if="contents.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <Link
        v-for="(content, index) in contents"
        :key="content.id"
        :href="`/student/library/${content.slug}`"
        class="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all cursor-pointer"
      >
        <!-- Thumbnail -->
        <div :class="['relative h-48 overflow-hidden bg-gradient-to-br', getGradientClass(index)]">
          <div class="absolute inset-0 flex items-center justify-center">
            <Play class="w-12 h-12 text-white opacity-80" />
          </div>

          <!-- New Badge -->
          <div v-if="content.is_new" class="absolute top-3 left-3">
            <span class="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">JUST PUBLISHED</span>
          </div>

          <!-- Bookmark Button -->
          <div v-if="!content.is_new" class="absolute top-3 right-3">
            <button class="bg-white/20 text-white p-2 rounded-lg hover:bg-white/30">
              <Bookmark class="w-4 h-4" />
            </button>
          </div>

          <!-- Duration -->
          <div class="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2 py-1 rounded">
            <template v-if="content.progress && content.progress > 0 && content.progress < 100">
              {{ Math.floor((content.progress / 100) * parseInt(content.duration.split(':')[0])) }}:{{ content.duration.split(':')[1] }} / {{ content.duration }}
            </template>
            <template v-else>
              {{ content.duration }}
            </template>
          </div>
        </div>

        <!-- Content Info -->
        <div class="p-5">
          <!-- Tags -->
          <div class="flex items-center space-x-2 mb-3">
            <span :class="['text-xs font-semibold px-2 py-1 rounded', getSubjectBadgeClass(content.subject)]">
              {{ content.subject }}
            </span>
            <span class="bg-purple-100 text-purple-700 text-xs font-semibold px-2 py-1 rounded">
              {{ content.type || 'Video' }}
            </span>
          </div>

          <!-- Title -->
          <h3 class="font-bold text-gray-900 text-base mb-2">{{ content.title }}</h3>

          <!-- Tutor & Date -->
          <p class="text-sm text-gray-600 mb-4">{{ content.tutor_name }} • {{ content.updated_at || 'Recently updated' }}</p>

          <!-- Progress or Status -->
          <template v-if="content.progress && content.progress > 0 && content.progress < 100">
            <div class="flex items-center justify-between mb-4">
              <div class="flex-1 bg-gray-200 rounded-full h-2 mr-3">
                <div
                  :class="['h-2 rounded-full', getProgressBarClass(index)]"
                  :style="{ width: `${content.progress}%` }"
                ></div>
              </div>
              <span class="text-xs font-semibold text-gray-700">{{ content.progress }}%</span>
            </div>
            <button :class="['w-full text-white py-2.5 rounded-lg text-sm font-semibold transition-colors', getButtonClass(index)]">
              Continue Watching
            </button>
          </template>
          <template v-else-if="content.progress === 100">
            <div class="flex items-center justify-between mb-4">
              <span class="text-xs text-gray-500">Completed</span>
              <span class="text-xs font-semibold text-green-600">&#10003; Watched</span>
            </div>
            <button :class="['w-full text-white py-2.5 rounded-lg text-sm font-semibold transition-colors', getButtonClass(index)]">
              Watch Again
            </button>
          </template>
          <template v-else>
            <div class="flex items-center justify-between mb-4">
              <span class="text-xs text-gray-500">{{ content.is_new ? 'New content available' : 'Not started' }}</span>
              <span v-if="content.is_saved" class="text-xs font-semibold text-green-600">&#10003; Saved to library</span>
              <span v-else class="text-xs font-semibold text-purple-600">In your library</span>
            </div>
            <button :class="['w-full text-white py-2.5 rounded-lg text-sm font-semibold transition-colors', getButtonClass(index)]">
              Start Watching
            </button>
          </template>
        </div>
      </Link>
    </div>

    <!-- Empty State -->
    <div v-else class="bg-white rounded-xl border border-gray-200 p-12 text-center">
      <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <Video class="w-8 h-8 text-gray-400" />
      </div>
      <h3 class="text-lg font-semibold text-gray-900 mb-2">No content yet</h3>
      <p class="text-sm text-gray-500 mb-4">Subscribe to tutors to access their content library</p>
      <Link
        href="/student/explore"
        class="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
      >
        Browse Tutors
      </Link>
    </div>
  </section>
</template>
